/* eslint-disable prefer-promise-reject-errors */
import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import {
  readCampaign,
  createOrUpdateAdGroup,
  readAdGroup,
  ERROR_ENTITY,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  genericApiError,
  parseDateFromAPIFormat,
  formatDateToAPIFormat,
  getConnectedLinkedInCompanyPage,
} from '@bingads-webui-campaign-react/linkedin-data';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

export const useLinkedinAdGroupSettingsRepository = () => {
  const i18n = useContext(I18nContext);
  const { send } = useWizardService();
  const {
    scope: {
      values: {
        cid, aid, campaignId, adGroupId,
      },
    },
  } = useContext(ScopeContext);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInAdGroupSettigns',
    activityName: 'Repository',
  });

  const load = useCallback(() => {
    const loadCampaign = () => readCampaign(cid, aid, campaignId);
    const loadAdGroup = () => readAdGroup(cid, aid, campaignId, adGroupId);
    const loadCompanyPage = () => getConnectedLinkedInCompanyPage(cid, aid);

    const initialRequests = [
      loadCampaign(),
      loadAdGroup(),
      loadCompanyPage(),
    ];

    return Promise.all(initialRequests)
      .then(([
        campaign,
        adGroup,
        companyInfo,
      ]) => {
        const { TimeZone } = campaign;
        const {
          Name, StartDate, EndDate, Status,
        } = adGroup;
        const isMemberTokenRevoked = companyInfo && companyInfo.IsMemberTokenActive === false;

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isMemberTokenRevoked });

        return {
          Name,
          StartDate: parseDateFromAPIFormat(i18n, StartDate),
          EndDate: parseDateFromAPIFormat(i18n, EndDate),
          adGroupStatus: Status,
          campaignTimeZone: TimeZone,
          isMemberTokenRevoked,
        };
      })
      .catch((err) => {
        logger.error(`Failed to load ad group settings data: ${JSON.stringify(err)}`);
        throw err;
      });
  }, [cid, aid, campaignId, adGroupId, i18n, logger, send]);

  const save = useCallback(({ data }) => {
    const { Name, StartDate, EndDate } = data;

    const adGroup = {
      Id: adGroupId,
      Name,
      StartDate: formatDateToAPIFormat(i18n, StartDate),
      EndDate: formatDateToAPIFormat(i18n, EndDate),
      Campaign: { Id: campaignId },
    };

    return createOrUpdateAdGroup(adGroup)
      .then(() => {
        logger.trace('Ad group settings data saved successfully');
        return Promise.resolve({});
      })
      .catch((err) => {
        logger.error(`Failed to save ad group settings data: ${JSON.stringify(err)}`);

        const errors = _.get(err, 'errors');
        const serverErrors = errors
          ? mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n)
          : [genericApiError];

        return Promise.reject({ error: { serverErrors } });
      });
  }, [adGroupId, campaignId, i18n, logger]);

  return {
    load,
    save,
  };
};
